import { createAsyncThunk, createSlice, PayloadAction } from '@reduxjs/toolkit';
import { ApiError, ErrorCode, RequestState } from 'shared/api';

import { fetchCurrentCandidateEmail, getAvailableTenants, getCurrentUser, getUserInfoForCandidate } from '../api';
import { AuthStatus, AvailableTenant, CandidateAccessTokenConfig, User, UserInfo } from '../types';

interface AuthState {
    status: AuthStatus;
    userState: RequestState;
    availableTenantsState: RequestState;
    getUserAuthInfoState: RequestState;
    user: User | null;
    availableTenants: AvailableTenant[];
    accessToken: CandidateAccessTokenConfig;
    errorMessage?: string;
    errorCode?: string;
    currentCandidateEmail: string | null;
}

export const initialState: AuthState = {
    status: 'idle',
    user: null,
    currentCandidateEmail: null,
    availableTenants: [],
    accessToken: {
        token: tryGetLimitedAccessToken(),
        fullAccess: false,
        oldAvailabilityToken: tryGetOldAvailabilityToken()
    },
    userState: {
        status: 'idle'
    },
    availableTenantsState: {
        status: 'idle'
    },
    getUserAuthInfoState: {
        status: 'idle'
    }
};

const authSlice = createSlice({
    name: 'auth',
    initialState,
    reducers: {
        setAccessToken(state, action: PayloadAction<CandidateAccessTokenConfig>) {
            state.accessToken = action.payload;
            state.status = action.payload.token ? 'authenticated' : 'unauthorized';
        },
        setStatus(state, action: PayloadAction<AuthStatus>) {
            if (action.payload === state.status) {
                return;
            }

            if (action.payload === 'unauthorized') {
                return {
                    ...initialState,
                    status: 'unauthorized'
                };
            }

            state.status = action.payload;
        },
        setError(state, action: PayloadAction<{ message: string, errorCode?: string } | undefined>) {
            state.errorMessage = action.payload?.message;
            state.errorCode = action.payload?.errorCode;
        },
        setCurrentCandidateEmail(state, action: PayloadAction<string | null>) {
            state.currentCandidateEmail = action.payload;
        },
        resetAuth() {
            return initialState;
        }
    },
    extraReducers: builder => {
        builder
            .addCase(fetchUserThunk.pending, (state) => {
                state.userState = { status: 'pending' };
            })
            .addCase(fetchUserThunk.fulfilled, (state, { payload }) => {
                state.user = payload;
                state.userState = { status: 'success' };
            })
            .addCase(fetchUserThunk.rejected, (state, { payload }) => {
                state.userState = { status: 'error', errorCode: payload };
            })

            .addCase(fetchAvailableTenantsThunk.pending, (state) => {
                state.availableTenantsState = { status: 'pending' };
            })
            .addCase(fetchAvailableTenantsThunk.fulfilled, (state, { payload }) => {
                state.availableTenants = payload;
                state.availableTenantsState = { status: 'success' };
            })
            .addCase(fetchAvailableTenantsThunk.rejected, (state, { payload }) => {
                state.availableTenantsState = { status: 'error', errorCode: payload };
            })

            .addCase(getUserAuthInfo.pending, (state) => {
                state.status = 'pending';
                state.getUserAuthInfoState = { status: 'pending' };
            })
            .addCase(getUserAuthInfo.fulfilled, (state) => {
                state.getUserAuthInfoState = { status: 'success' };
            })
            .addCase(getUserAuthInfo.rejected, (state, { payload }) => {
                state.getUserAuthInfoState = { status: 'error', errorCode: payload };
            })
            .addCase(fetchCurrentCandidateEmailThunk.fulfilled, (state, { payload }) => {
                state.currentCandidateEmail = payload;
            });
    }
});

export const {
    setError,
    setStatus,
    setAccessToken,
    resetAuth,
    setCurrentCandidateEmail
} = authSlice.actions;

export default authSlice.reducer;

export const fetchUserThunk = createAsyncThunk<User, void, { rejectValue: ErrorCode }>(
    `${ authSlice.name }/fetchUser`,
    async (_, thunkAPI) => {
        const response = await getCurrentUser();

        if (response instanceof ApiError) {
            return thunkAPI.rejectWithValue(response.errorCode);
        }

        return response;
    }
);

export const fetchAvailableTenantsThunk = createAsyncThunk<AvailableTenant[], void, { rejectValue: ErrorCode }>(
    `${ authSlice.name }/fetchAvailableTenants`,
    async (_, thunkAPI) => {
        const response = await getAvailableTenants();

        if (response instanceof ApiError) {
            return thunkAPI.rejectWithValue(response.errorCode);
        }

        return response;
    }
);

export const getUserAuthInfo = createAsyncThunk<UserInfo, void, { rejectValue: ErrorCode }>(
    `${ authSlice.name }/getUserInfoForCandidate`,
    async (_, thunkAPI) => {
        const response = await getUserInfoForCandidate();

        if (response instanceof ApiError) {
            return thunkAPI.rejectWithValue(response.errorCode);
        }

        return response;
    }
);

export const fetchCurrentCandidateEmailThunk = createAsyncThunk<string, void, { rejectValue: ErrorCode }>(
    `${ authSlice.name }/fetchCurrentCandidateEmail`,
    async (_, thunkAPI) => {
        const response = await fetchCurrentCandidateEmail();

        if (response instanceof ApiError) {
            return thunkAPI.rejectWithValue(response.errorCode);
        }

        return response;
    }
);

function tryGetLimitedAccessToken() {
    const params = new URLSearchParams(window.location.search);

    const fromUrl = params.get('candidateToken');

    if (fromUrl) {
        sessionStorage.setItem('candidateToken', fromUrl);

        return fromUrl;
    }

    return sessionStorage.getItem('candidateToken') || null;
}

function tryGetOldAvailabilityToken() {
    const postfix = document.location.pathname.split('/')[2];
    const token = postfix ? postfix.split('?')[0] : '';

    if (token.length > 50 && document.location.search.startsWith('?tenantId=')) {
        return token;
    }

    return null;
}
